import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import { useClipboard } from "@galaxy-io/dls/hooks/useClipboard";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import TextAreaInput, { TextAreaInputVariant } from "@galaxy-io/dls/inputs/TextAreaInput";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import {
  type ProductRequest,
  type ProductSupporter,
  productRequestCreate,
  productSupporterInput,
} from "@gtm/contracts/core/product-requests";
import { PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { DrawerList, DrawerRow, DrawerStack } from "@/components/workspace/drawer-content";
import { PageHeader } from "@/components/workspace/page-header";
import { FeedViewport } from "@/components/workspace/people-styles";
import { RecordTable } from "@/components/workspace/record-table";
import { SourceMarkdown } from "@/components/workspace/source-markdown";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { date } from "@/lib/utils";

const QUERY_KEY = ["product-requests"];

export function ProductRequestsView() {
  const { params, update } = useUrlState();
  const clipboard = useClipboard();

  const client = useQueryClient();
  const { toast } = useToast();
  const [editor, setEditor] = useState<ProductRequest | "new" | null>(null);
  const [supporterEditor, setSupporterEditor] = useState<{
    request: ProductRequest;
    supporter?: ProductSupporter;
  } | null>(null);
  const [removing, setRemoving] = useState<{
    request: ProductRequest;
    supporter?: ProductSupporter;
  } | null>(null);
  const search = params.get("q") ?? "";
  const selected = params.get("request");
  const requests = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => unwrap(apiClient().GET("/product-requests", {})),
  });
  const entry = requests.data?.items.find((item) => item.id === selected);
  const term = search.trim().toLowerCase();
  const items = (requests.data?.items ?? []).filter((r) =>
    [r.title, r.notes, ...r.supporters.flatMap((s) => [s.email, s.notes])].some((value) =>
      value.toLowerCase().includes(term),
    ),
  );
  const retainedEditor = useRetainedValue(editor);
  const retainedSupporterEditor = useRetainedValue(supporterEditor);
  const retainedEntry = useRetainedValue(entry);

  async function copyEmails(item: ProductRequest) {
    if (await clipboard.copy(item.supporters.map((s) => s.email).join(", "))) {
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Emails copied",
        description: "Ready for your follow-up.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } else {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not copy emails",
        description: "Select the addresses below and copy them manually.",
        ...toastDefaults(ToastVariant.ERROR),
      });
    }
  }
  async function remove() {
    if (!removing) return;
    if (removing.supporter)
      await unwrap(
        apiClient().DELETE("/product-requests/{id}/supporters/{supporterId}", {
          params: { path: { id: removing.request.id, supporterId: removing.supporter.id } },
        }),
      );
    else
      await unwrap(
        apiClient().DELETE("/product-requests/{id}", {
          params: { path: { id: removing.request.id } },
        }),
      );
    await client.invalidateQueries({ queryKey: QUERY_KEY });
    if (!removing.supporter) update({ request: null });
    setRemoving(null);
  }
  return (
    <>
      <PageHeader
        title="Product Requests"
        actions={
          <Button label="New request" onClick={() => setEditor("new")} size={ButtonSize.MEDIUM} />
        }
      />
      <BaseToolbar
        leadingActions={
          <Input
            aria-label="Search product requests"
            placeholder="Search requests, emails or notes…"
            value={search}
            onChange={(e) => update({ q: e || null }, true)}
            type="search"
            size={InputSize.SMALL}
            label="Search product requests"
            hideLabel
          />
        }
      />
      <FeedViewport>
        <RecordTable
          label="Product requests · most supporters first"
          isLoading={requests.isPending}
          error={requests.error || undefined}
          onRetry={() => void requests.refetch()}
          rowKeys={items.map((r) => r.id)}
          onRowClick={(id) => update({ request: id })}
          columns={[
            { key: "title", label: "Request", width: 300, pin: "left" },
            { key: "supporters", label: "Supporters ↓", width: 130, align: "right" as const },
            { key: "notes", label: "Notes", width: "auto" },
            { key: "updated", label: "Updated", width: 190 },
          ]}
          rows={items.map((r) => ({
            title: (
              <Text size={TextSize.BODY_SM} lineClamp={1}>
                {r.title}
              </Text>
            ),
            supporters: r.supporterCount.toLocaleString(),
            notes: (
              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={1}>
                {r.notes || "—"}
              </Text>
            ),
            updated: date(r.updatedAt, true),
          }))}
        />
      </FeedViewport>
      {selected && !entry && (
        <SurfaceDialog
          open
          drawer
          title="Product request"
          onClose={() => update({ request: null })}
        >
          <QueryState
            error={requests.error ?? (requests.isPending ? null : "Product request not found.")}
            loadingMessage="Loading…"
            onRetry={() => void requests.refetch()}
          />
        </SurfaceDialog>
      )}
      {retainedEntry && (
        <SurfaceDialog
          open={!!entry}
          drawer
          flush
          title={retainedEntry.title}
          onClose={() => update({ request: null })}
          headerActions={
            <>
              <Button
                ariaLabel="Edit"
                tooltip="Edit"
                icon={PencilSimpleIcon}
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TERTIARY}
                onClick={() => setEditor(retainedEntry)}
              />
              <Button
                ariaLabel="Delete"
                tooltip="Delete"
                icon={TrashIcon}
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TERTIARY}
                onClick={() => setRemoving({ request: retainedEntry })}
              />
            </>
          }
        >
          <DrawerStack>
            <DrawerList>
              <DescriptionList
                items={[
                  { label: "Supporters", value: retainedEntry.supporterCount },
                  {
                    label: "Added",
                    value: date(retainedEntry.createdAt, true),
                    family: FontFamily.MONO,
                  },
                  {
                    label: "Updated",
                    value: date(retainedEntry.updatedAt, true),
                    family: FontFamily.MONO,
                  },
                ]}
              />
            </DrawerList>
            <Text>Notes</Text>
            {retainedEntry.notes ? (
              <SourceMarkdown content={retainedEntry.notes} breaks />
            ) : (
              <EmptyState header="No notes yet" description="Edit this request to add context." />
            )}
            <BaseToolbar
              leadingActions={<Text>Supporters ({retainedEntry.supporterCount})</Text>}
              trailingActions={
                <>
                  <Button
                    label="Copy emails"
                    variant={ButtonVariant.SECONDARY}
                    isDisabled={!retainedEntry.supporterCount}
                    size={ButtonSize.SMALL}
                    onClick={() => void copyEmails(retainedEntry)}
                  />
                  <Button
                    label="Add supporter"
                    onClick={() => setSupporterEditor({ request: retainedEntry })}
                    size={ButtonSize.SMALL}
                  />
                </>
              }
            />
            {!retainedEntry.supporters.length && (
              <Text variant={TextVariant.SECONDARY}>
                Add the email addresses of people using or requesting this functionality. They don’t
                need a People record.
              </Text>
            )}
            {retainedEntry.supporters.map((s) => (
              <DrawerList key={s.id}>
                <DrawerRow label="Email">{s.email}</DrawerRow>
                {s.notes && (
                  <DrawerStack>
                    <SourceMarkdown content={s.notes} breaks />
                  </DrawerStack>
                )}
                <DescriptionList
                  items={[
                    { label: "Added", value: date(s.createdAt, true), family: FontFamily.MONO },
                    {
                      label: "Actions",
                      value: (
                        <>
                          <Button
                            label="Edit notes"
                            variant={ButtonVariant.TERTIARY}
                            onClick={() =>
                              setSupporterEditor({ request: retainedEntry, supporter: s })
                            }
                          />
                          <Button
                            label="Remove"
                            variant={ButtonVariant.TERTIARY}
                            onClick={() => setRemoving({ request: retainedEntry, supporter: s })}
                          />
                        </>
                      ),
                    },
                  ]}
                />
              </DrawerList>
            ))}
          </DrawerStack>
        </SurfaceDialog>
      )}
      <RequestEditor
        open={!!editor}
        key={retainedEditor === "new" ? "new" : retainedEditor?.id}
        entry={retainedEditor === "new" ? undefined : retainedEditor}
        onClose={() => setEditor(null)}
        onSaved={(id) => {
          setEditor(null);
          update({ request: id });
        }}
      />
      {retainedSupporterEditor && (
        <SupporterEditor
          open={!!supporterEditor}
          request={retainedSupporterEditor.request}
          supporter={retainedSupporterEditor.supporter}
          onClose={() => setSupporterEditor(null)}
        />
      )}
      <ConfirmAction
        isOpen={!!removing}
        header={removing?.supporter ? "Remove supporter?" : "Delete product request?"}
        description={
          removing?.supporter
            ? `${removing.supporter.email} will be removed from this request.`
            : `“${removing?.request.title ?? ""}” and its supporter associations will be removed from this workspace’s board.`
        }
        label={removing?.supporter ? "Remove supporter" : "Delete request"}
        isDestructive
        onOpenChange={(next) => {
          if (!next) (() => setRemoving(null))();
        }}
        onConfirm={async () => remove()}
      />
    </>
  );
}
function RequestEditor({
  open = true,
  entry,
  onClose,
  onSaved,
}: {
  entry?: ProductRequest;
  onClose: () => void;
  onSaved: (id: string) => void;
  open?: boolean;
}) {
  const client = useQueryClient();
  const [title, setTitle] = useState(entry?.title ?? "");
  const [notes, setNotes] = useState(entry?.notes ?? "");
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const changed = title !== (entry?.title ?? "") || notes !== (entry?.notes ?? "") || !!emails;
  useDialogReset(open, () => {
    setTitle(entry?.title ?? "");
    setNotes(entry?.notes ?? "");
    setEmails("");
    setBusy(false);
    setError("");
  });
  async function save() {
    const parsed = productRequestCreate.safeParse({
      title,
      notes,
      supporters: emails
        .split(/[\s,;]+/)
        .filter(Boolean)
        .map((email) => ({ email })),
    });
    if (!parsed.success) {
      setError("Enter a request title and valid email addresses.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      let id = entry?.id;
      if (id)
        await unwrap(
          apiClient().PUT("/product-requests/{id}", {
            params: { path: { id } },
            body: { title: parsed.data.title, notes: parsed.data.notes },
          }),
        );
      else id = (await unwrap(apiClient().POST("/product-requests", { body: parsed.data }))).id;
      await client.invalidateQueries({ queryKey: QUERY_KEY });
      onSaved(id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <SurfaceDialog
      open={open}
      flush
      title={entry ? "Edit product request" : "New product request"}
      headerDescription="Keep feature requests and use cases together with the people who need them."
      onClose={onClose}
      dirty={changed}
      busy={busy}
      footer={
        <>
          <Button
            label="Cancel"
            variant={ButtonVariant.SECONDARY}
            isDisabled={busy}
            onClick={onClose}
          />
          <Button
            label="Save request"
            isLoading={busy}
            isDisabled={busy || !title.trim() || !changed}
            onClick={() => void save()}
          />
        </>
      }
    >
      <DrawerStack>
        <TextAreaInput
          label="Request"
          value={title}
          onChange={setTitle}
          minRows={2}
          fillWidth
          variant={TextAreaInputVariant.BASE}
          isDisabled={busy}
          placeholder="MongoDB support"
        />
        <TextAreaInput
          label="Notes (optional)"
          value={notes}
          onChange={setNotes}
          minRows={8}
          fillWidth
          variant={TextAreaInputVariant.BASE}
          isDisabled={busy}
          placeholder="What are they using or requesting? Include the use case and any call notes…"
        />
        {!entry && (
          <TextAreaInput
            label="Supporter emails (optional)"
            value={emails}
            onChange={setEmails}
            minRows={3}
            fillWidth
            variant={TextAreaInputVariant.BASE}
            isDisabled={busy}
            placeholder="Separate email addresses with commas or new lines"
          />
        )}
        <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
          Shared with this workspace. Each email counts once per request. Notes support Markdown.
        </Text>
        {error && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
      </DrawerStack>
    </SurfaceDialog>
  );
}
function SupporterEditor({
  open = true,
  request,
  supporter,
  onClose,
}: {
  request: ProductRequest;
  supporter?: ProductSupporter;
  onClose: () => void;
  open?: boolean;
}) {
  const client = useQueryClient();
  const [email, setEmail] = useState(supporter?.email ?? "");
  const [notes, setNotes] = useState(supporter?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const changed = email !== (supporter?.email ?? "") || notes !== (supporter?.notes ?? "");
  useDialogReset(open, () => {
    setEmail(supporter?.email ?? "");
    setNotes(supporter?.notes ?? "");
    setBusy(false);
    setError("");
  });
  async function save() {
    const parsed = productSupporterInput.safeParse({
      email,
      ...(supporter || notes.trim() ? { notes } : {}),
    });
    if (!parsed.success) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await unwrap(
        apiClient().POST("/product-requests/{id}/supporters", {
          params: { path: { id: request.id } },
          body: parsed.data,
        }),
      );
      await client.invalidateQueries({ queryKey: QUERY_KEY });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <SurfaceDialog
      open={open}
      flush
      title={supporter ? "Edit supporter notes" : "Add supporter"}
      headerDescription={request.title}
      onClose={onClose}
      dirty={changed}
      busy={busy}
      footer={
        <>
          <Button
            label="Cancel"
            variant={ButtonVariant.SECONDARY}
            isDisabled={busy}
            onClick={onClose}
          />
          <Button
            label="Save supporter"
            isLoading={busy}
            isDisabled={busy || !email.trim() || !changed}
            onClick={() => void save()}
          />
        </>
      }
    >
      <DrawerStack>
        <Input
          aria-label="Supporter email"
          placeholder="Email address"
          type="email"
          value={email}
          disabled={busy || !!supporter}
          onChange={(e) => setEmail(e)}
          label="Supporter email"
        />
        <TextAreaInput
          label="Context (optional)"
          value={notes}
          onChange={setNotes}
          minRows={6}
          fillWidth
          variant={TextAreaInputVariant.BASE}
          isDisabled={busy}
          placeholder="Name, company, what they need, or notes from the call…"
        />
        <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
          No People record is required. An existing email won’t be counted twice. Saving context for
          an existing email replaces its previous notes.
        </Text>
        {error && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
      </DrawerStack>
    </SurfaceDialog>
  );
}
